import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { Mail, Send, CheckCircle, Archive, RotateCcw } from 'lucide-react';
import { FloorTheme } from '@/lib/themes';
import api, {
    archiveInquiry,
    getAdminInquiriesByArchive,
    getAdminSentInquiriesByArchive,
    unarchiveInquiry,
    uploadFile,
} from '@/lib/api';
import { getImageFileFromClipboard, insertTextAtCursor } from '@/utils/pasteImage';
import { autoResizeTextarea } from '@/utils/autoResize';
import { getInputSurface, getMutedTextColor, getPanelStyle, getSoftSurface, isDarkTheme, pickTextColor, withAlpha } from '@/lib/themeStyles';

type MailboxView = 'inbox' | 'sent' | 'archive_inbox' | 'archive_sent';

type MailboxItem = {
    contact_id: number;
    category: string;
    message: string;
    created_at: string;
    is_resolved: boolean;
    inquiry_number?: string | null;
    attachment_urls?: string[];
    is_read?: boolean;
    is_archived?: boolean;
    sender_name?: string | null;
    sender_email?: string | null;
    sender_username?: string | null;
    sender_role?: string | null;
    recipient_name?: string | null;
    recipient_email?: string | null;
    recipient_username?: string | null;
    counterparty_name?: string | null;
    counterparty_email?: string | null;
    counterparty_username?: string | null;
    direction: 'inbox' | 'sent';
};

export const Mailbox: React.FC = () => {
    // @ts-ignore
    const { theme } = useOutletContext<{ theme: FloorTheme }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const [activeBox, setActiveBox] = useState<MailboxView>('inbox');
    const [items, setItems] = useState<MailboxItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [replySubject, setReplySubject] = useState('');
    const [replyBody, setReplyBody] = useState('');
    const replyBodyRef = useRef<HTMLTextAreaElement>(null);
    const [sending, setSending] = useState(false);
    const [feedback, setFeedback] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
    const [pendingContactId, setPendingContactId] = useState<number | null>(null);

    const [filterUsername, setFilterUsername] = useState('');
    const [filterEmail, setFilterEmail] = useState('');
    const [filterInquiryNumber, setFilterInquiryNumber] = useState('');
    const darkTheme = isDarkTheme(theme);
    const panelStyle = getPanelStyle(theme, 0.96, 0.9);
    const mutedTextColor = getMutedTextColor(theme);
    const inputSurface = getInputSurface(theme);

    const isInbox = activeBox === 'inbox' || activeBox === 'archive_inbox';
    const isArchive = activeBox === 'archive_inbox' || activeBox === 'archive_sent';
    const isVideoUrl = (url: string) => /\.(mp4|webm|mov|m4v)$/i.test(url.split('?')[0]);

    useEffect(() => {
        fetchItems();
        setSelectedId(null);
    }, [activeBox]);

    useEffect(() => {
        const rawContactId = searchParams.get('contact_id');
        if (!rawContactId) return;
        const contactId = Number(rawContactId);
        if (!Number.isFinite(contactId) || contactId <= 0) {
            const next = new URLSearchParams(searchParams);
            next.delete('contact_id');
            setSearchParams(next, { replace: true });
            return;
        }
        setPendingContactId(contactId);
        if (activeBox !== 'inbox') {
            setActiveBox('inbox');
        }
    }, [searchParams, setSearchParams, activeBox]);

    useEffect(() => {
        autoResizeTextarea(replyBodyRef.current);
    }, [replyBody]);

    useEffect(() => {
        if (!pendingContactId || loading) return;
        const target = items.find((item) => item.contact_id === pendingContactId);

        const next = new URLSearchParams(searchParams);
        next.delete('contact_id');
        setSearchParams(next, { replace: true });
        setPendingContactId(null);

        if (target) {
            handleSelect(target);
        }
    }, [pendingContactId, loading, items, searchParams, setSearchParams]);

    const fetchItems = async () => {
        setLoading(true);
        try {
            const archived = activeBox === 'archive_inbox' || activeBox === 'archive_sent';
            if (isInbox) {
                const res = await getAdminInquiriesByArchive(archived);
                const normalized: MailboxItem[] = res.data.map((item: any) => ({
                    ...item,
                    direction: 'inbox',
                    counterparty_name: item.sender_name,
                    counterparty_email: item.sender_email,
                    counterparty_username: item.sender_username,
                }));
                setItems(normalized);
            } else {
                const res = await getAdminSentInquiriesByArchive(archived);
                const normalized: MailboxItem[] = res.data.map((item: any) => ({
                    ...item,
                    direction: 'sent',
                    counterparty_name: item.recipient_name,
                    counterparty_email: item.recipient_email,
                    counterparty_username: item.recipient_username,
                }));
                setItems(normalized);
            }
        } catch (e) {
            console.error('Failed to fetch mailbox items', e);
        } finally {
            setLoading(false);
        }
    };

    const filteredItems = useMemo(() => {
        const nameQuery = filterUsername.trim().toLowerCase();
        const emailQuery = filterEmail.trim().toLowerCase();
        const inquiryQuery = filterInquiryNumber.trim().toLowerCase();

        return items.filter((item) => {
            const name = (item.counterparty_username || item.counterparty_name || '').toLowerCase();
            const email = (item.counterparty_email || '').toLowerCase();
            const inquiry = (item.inquiry_number || '').toLowerCase();

            if (nameQuery && !name.includes(nameQuery)) return false;
            if (emailQuery && !email.includes(emailQuery)) return false;
            if (inquiryQuery && !inquiry.includes(inquiryQuery)) return false;
            return true;
        });
    }, [items, filterUsername, filterEmail, filterInquiryNumber]);

    const selectedItem = filteredItems.find((i) => i.contact_id === selectedId);

    async function handleSelect(item: MailboxItem) {
        setSelectedId(item.contact_id);
        setFeedback(null);

        if (item.direction === 'inbox') {
            setReplySubject(`Re: [Maison Portal] ${item.category}`);
            setReplyBody(`\n\n> ${item.message.split('\n').join('\n> ')}`);

            if (!item.is_read) {
                try {
                    await api.post(`/admin/inquiries/${item.contact_id}/read`);
                    setItems((prev) =>
                        prev.map((i) => (i.contact_id === item.contact_id ? { ...i, is_read: true } : i))
                    );
                } catch (e) {
                    console.error('Failed to mark as read', e);
                }
            }
        }
    }

    const handleSend = async () => {
        if (!selectedItem || selectedItem.direction !== 'inbox') return;
        setSending(true);
        setFeedback(null);

        try {
            await api.post(`/admin/inquiries/${selectedItem.contact_id}/reply`, {
                subject: replySubject,
                body: replyBody,
                resolve: true,
            });
            setFeedback({ msg: 'Reply sent successfully.', type: 'success' });
            setItems((prev) =>
                prev.map((i) => (i.contact_id === selectedItem.contact_id ? { ...i, is_resolved: true } : i))
            );
        } catch (e) {
            setFeedback({ msg: 'Network error.', type: 'error' });
        } finally {
            setSending(false);
        }
    };

    const handlePasteReplyImage = async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
        const imageFile = getImageFileFromClipboard(e);
        if (!imageFile) return;
        e.preventDefault();
        if (!imageFile.type.startsWith('image/')) return;
        try {
            const res = await uploadFile(imageFile);
            const url = res.data?.url;
            if (url) {
                insertTextAtCursor(e.currentTarget, replyBody, `\n${url}\n`, setReplyBody);
            }
        } catch (err) {
            console.error('Paste upload failed', err);
        }
    };

    const handleArchiveToggle = async () => {
        if (!selectedItem) return;
        try {
            if (isArchive) {
                await unarchiveInquiry(selectedItem.contact_id);
            } else {
                await archiveInquiry(selectedItem.contact_id);
            }
            setItems((prev) => prev.filter((i) => i.contact_id !== selectedItem.contact_id));
            setSelectedId(null);
        } catch (e) {
            console.error('Archive toggle failed', e);
        }
    };

    const unreadCount = items.filter((i) => i.direction === 'inbox' && !i.is_read).length;

    return (
        <div className={`p-6 min-h-screen ${theme.font} transition-colors duration-500`}>
            <div className="max-w-6xl mx-auto">
                <div className="flex flex-wrap gap-2 mb-4">
                    {[
                        { key: 'inbox', label: 'Inbox' },
                        { key: 'sent', label: 'Sent' },
                        { key: 'archive_inbox', label: 'Inbox Archive' },
                        { key: 'archive_sent', label: 'Sent Archive' },
                    ].map((tab) => (
                        <button
                            key={tab.key}
                            onClick={() => setActiveBox(tab.key as MailboxView)}
                            className={`px-4 py-2 rounded-lg text-sm font-bold border transition-all ${
                                activeBox === tab.key ? 'opacity-100' : 'opacity-60 hover:opacity-90'
                            }`}
                            style={{
                                borderColor: activeBox === tab.key ? theme.colors.primary : theme.colors.border,
                                backgroundColor: activeBox === tab.key ? withAlpha(theme.colors.primary, darkTheme ? 0.18 : 0.1) : 'transparent',
                                color: activeBox === tab.key ? theme.colors.primary : theme.colors.text,
                            }}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 h-[80vh]">
                    {/* LIST */}
                    <div
                        className="col-span-1 rounded-2xl overflow-hidden border flex flex-col"
                        style={panelStyle}
                    >
                        <div className="p-4 border-b" style={{ borderColor: theme.colors.border }}>
                            <div className="flex justify-between items-center">
                                <h2 className="text-lg font-bold" style={{ color: theme.colors.text }}>
                                    {activeBox === 'inbox' && 'Inbox'}
                                    {activeBox === 'sent' && 'Sent'}
                                    {activeBox === 'archive_inbox' && 'Inbox Archive'}
                                    {activeBox === 'archive_sent' && 'Sent Archive'}
                                </h2>
                                {isInbox && !isArchive && (
                                    <span
                                        className="text-sm px-2 py-0.5 rounded-full"
                                        style={{
                                            backgroundColor: getSoftSurface(theme, 0.82, 0.46),
                                            color: theme.colors.text,
                                            borderColor: theme.colors.border,
                                            borderWidth: '1px',
                                        }}
                                    >
                                        {unreadCount} Unread
                                    </span>
                                )}
                            </div>
                            <div className="mt-4 space-y-2">
                                <input
                                    type="text"
                                    value={filterUsername}
                                    onChange={(e) => setFilterUsername(e.target.value)}
                                    className="w-full p-2 rounded border text-sm"
                                    placeholder="Filter by username"
                                    style={{ borderColor: theme.colors.border, backgroundColor: inputSurface, color: theme.colors.text }}
                                />
                                <input
                                    type="text"
                                    value={filterEmail}
                                    onChange={(e) => setFilterEmail(e.target.value)}
                                    className="w-full p-2 rounded border text-sm"
                                    placeholder="Filter by email"
                                    style={{ borderColor: theme.colors.border, backgroundColor: inputSurface, color: theme.colors.text }}
                                />
                                <input
                                    type="text"
                                    value={filterInquiryNumber}
                                    onChange={(e) => setFilterInquiryNumber(e.target.value)}
                                    className="w-full p-2 rounded border text-sm"
                                    placeholder="Filter by inquiry number"
                                    style={{ borderColor: theme.colors.border, backgroundColor: inputSurface, color: theme.colors.text }}
                                />
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto">
                            {loading && <div className="p-4">Loading...</div>}
                            {!loading && filteredItems.length === 0 && (
                                <div className="p-4 text-sm opacity-60">No messages found.</div>
                            )}
                            {!loading &&
                                filteredItems.map((item) => (
                                    <div
                                        key={item.contact_id}
                                        onClick={() => handleSelect(item)}
                                        className={`p-4 border-b cursor-pointer transition-colors relative ${
                                            selectedId === item.contact_id ? 'opacity-100' : 'opacity-80 hover:opacity-100'
                                        }`}
                                        style={{
                                            borderColor: theme.colors.border,
                                            backgroundColor: selectedId === item.contact_id ? theme.colors.secondary : 'transparent',
                                            color: theme.colors.text,
                                        }}
                                    >
                                        <div className="flex justify-between mb-1">
                                            <span className="font-bold text-sm truncate">{item.counterparty_name || 'Unknown'}</span>
                                            {item.direction === 'inbox' && !item.is_read && <span className="w-2 h-2 rounded-full bg-red-500" />}
                                        </div>
                                        <div className="text-xs opacity-70 mb-1">{new Date(item.created_at).toLocaleDateString()}</div>
                                        <div className="text-sm font-medium truncate">{item.category}</div>
                                        {item.inquiry_number && (
                                            <div className="text-xs opacity-60 truncate">No: {item.inquiry_number}</div>
                                        )}
                                        {item.direction === 'sent' && (item.sender_name || item.sender_username) && (
                                            <div className="text-xs opacity-60 truncate">
                                                By: {item.sender_name || item.sender_username}
                                                {item.sender_role ? ` (${item.sender_role})` : ''}
                                            </div>
                                        )}
                                        <div className="text-xs opacity-60 truncate">{item.message}</div>
                                        {item.is_resolved && <CheckCircle className="w-3 h-3 text-green-500 absolute bottom-4 right-4" />}
                                    </div>
                                ))}
                        </div>
                    </div>

                    {/* DETAIL & REPLY */}
                    <div
                        className="col-span-1 md:col-span-2 rounded-2xl overflow-hidden border flex flex-col relative"
                        style={panelStyle}
                    >
                        {selectedItem ? (
                            <>
                                <div className="p-6 border-b" style={{ borderColor: theme.colors.border }}>
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <h1 className="text-xl font-bold mb-2">{selectedItem.category}</h1>
                                            <div className="flex items-center gap-2 text-sm opacity-80">
                                                <Mail className="w-4 h-4" />
                                                <span>
                                                    {selectedItem.direction === 'inbox' ? 'From: ' : 'To: '}
                                                    {selectedItem.counterparty_name} &lt;{selectedItem.counterparty_email || 'unknown'}&gt;
                                                </span>
                                            </div>
                                            {selectedItem.inquiry_number && (
                                                <div className="text-xs opacity-70 mt-1">Inquiry No: {selectedItem.inquiry_number}</div>
                                            )}
                                            {selectedItem.direction === 'sent' && (selectedItem.sender_name || selectedItem.sender_username) && (
                                                <div className="text-xs opacity-70 mt-1">
                                                    Sent by: {selectedItem.sender_name || selectedItem.sender_username}
                                                    {selectedItem.sender_role ? ` (${selectedItem.sender_role})` : ''}
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {selectedItem.is_resolved && (
                                                <span
                                                    className="px-3 py-1 rounded-full text-xs font-bold border"
                                                    style={{
                                                        backgroundColor: withAlpha(theme.colors.primary, darkTheme ? 0.16 : 0.12),
                                                        color: theme.colors.primary,
                                                        borderColor: withAlpha(theme.colors.primary, 0.4),
                                                    }}
                                                >
                                                    RESOLVED
                                                </span>
                                            )}
                                            <button
                                                onClick={handleArchiveToggle}
                                                className="px-3 py-1 rounded-full text-xs font-bold border flex items-center gap-1"
                                                style={{ borderColor: theme.colors.border, color: theme.colors.text }}
                                            >
                                                {isArchive ? <RotateCcw className="w-3 h-3" /> : <Archive className="w-3 h-3" />}
                                                {isArchive ? 'Restore' : 'Archive'}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="p-6 overflow-y-auto max-h-[40vh] border-b" style={{ borderColor: theme.colors.border }}>
                                    <p className="whitespace-pre-wrap leading-relaxed">{selectedItem.message}</p>
                                    {selectedItem.attachment_urls && selectedItem.attachment_urls.length > 0 && (
                                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            {selectedItem.attachment_urls.map((url) => (
                                                <div key={url} className="border rounded-lg overflow-hidden" style={{ borderColor: theme.colors.border }}>
                                                    {isVideoUrl(url) ? (
                                                        <video src={url} controls className="w-full max-h-48 bg-black" />
                                                    ) : (
                                                        <img src={url} alt="Attachment" className="w-full max-h-48 object-cover" />
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {selectedItem.direction === 'inbox' ? (
                                    <div
                                        className="p-6 flex-1 flex flex-col gap-4"
                                        style={{
                                            backgroundColor: getSoftSurface(theme, 0.86, 0.42),
                                        }}
                                    >
                                        <input
                                            type="text"
                                            value={replySubject}
                                            onChange={(e) => setReplySubject(e.target.value)}
                                            className="w-full p-2 rounded border focus:outline-none focus:ring-2"
                                            style={{
                                                borderColor: theme.colors.border,
                                                backgroundColor: inputSurface,
                                                color: theme.colors.text,
                                                '--tw-ring-color': theme.colors.primary,
                                            } as any}
                                            placeholder="Subject"
                                        />
                                        <textarea
                                            ref={replyBodyRef}
                                            value={replyBody}
                                            onChange={(e) => setReplyBody(e.target.value)}
                                            onInput={(e) => autoResizeTextarea(e.currentTarget)}
                                            onPaste={handlePasteReplyImage}
                                            className="w-full flex-1 p-2 rounded border focus:outline-none focus:ring-2 resize-none"
                                            style={{
                                                borderColor: theme.colors.border,
                                                backgroundColor: inputSurface,
                                                color: theme.colors.text,
                                                '--tw-ring-color': theme.colors.primary,
                                            } as any}
                                            placeholder="Type your reply here..."
                                        />
                                        <div className="flex justify-between items-center mt-2">
                                            {feedback && (
                                                <span
                                                    className="text-sm"
                                                    style={{ color: feedback.type === 'success' ? theme.colors.primary : '#ef4444' }}
                                                >
                                                    {feedback.msg}
                                                </span>
                                            )}
                                            <button
                                                onClick={handleSend}
                                                disabled={sending}
                                                className={`px-6 py-2 rounded-lg text-white font-bold flex items-center gap-2 transition-transform hover:scale-105 ${
                                                    sending ? 'opacity-50 cursor-not-allowed' : ''
                                                }`}
                                                style={{ backgroundColor: theme.colors.primary, color: pickTextColor(theme.colors.primary) }}
                                            >
                                                <Send className="w-4 h-4" />
                                                {sending ? 'Sending...' : 'Send Reply'}
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-center h-full" style={{ color: mutedTextColor }}>Reply is available in Inbox.</div>
                                )}
                            </>
                        ) : (
                            <div className="flex items-center justify-center h-full" style={{ color: mutedTextColor }}>Select a message to view</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
